import { Logger, ArrayUtils } from "neo-common";
import { Communication } from "neo-games";
import { Settings } from "./Settings";
import {
    Configuration,
    GeneralConfigurationAttributes,
    MachineConfigurationAttributes,
} from "../Components/Configuration";
import { PlayList } from "../Components/Sounds/PlayList";

type IReel = Communication.Game.IReel;

export interface ICheckOptions {
    force: boolean;
    stoppedSymbols: Array<IReel>;
}

export interface IReelTeasingAttributes {
    action: number;
    stopping: boolean;
    stopDelay?: number;
}

export interface ISymbolTeasingAttributes {
    teasable: boolean;
    action?: number;
}

export const ReelTeasingActions = {
    DO_NOTHING: 0,
    SPIN_STOP: 1,
    TEASE: 2,
    FORCE_SPIN_STOP: 3, // todo Use this?
};

export const SymbolTeasingActions = {
    NOWIN: 0,
    NORMAL: 1,
    IMPACT: 2,
    IMPACT_NOWIN: 3,
    TEASE: 4,
    IMPACT_TEASE: 5,
};

/**
 * @author Marek Turkovic
 */
export class TeasingComponent {
    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    // count of reel
    protected reelCount: number;
    // number of symbols which will be visible on screen during the rotation
    private _visibleSymbolCount: number;
    // reels x positions
    protected reelX: Array<number>;
    // reels y positions
    protected reelY: Array<number>;
    // delay to stop rotation in frames
    protected stopDelay: number;
    // teased symbol
    protected teasingSymbol: number = -1;

    protected impactSymbols: Array<number> = [];

    // delay to stop teasing rotation in frames
    protected teasingStopDelay: number;
    protected minimumSymbolsForTeasing: number;
    protected countSymbolsOnReelsAndRows: number;

    protected canPlayTeaserSound: boolean;
    protected wasPlayedTeaserSound: boolean;
    protected wasPlayedTeaserWinSound: boolean;
    protected wasPlayedTeaserFailSound: boolean;

    protected elementInstance: Array<Object> = [];

    // array of stopped symbols for finish reel rotation
    protected stoppedSymbolsArray: Array<IReel>;

    // teasing enabled in current round ?
    public teasingEnabled: boolean = false; // todo create getter for public access ?
    public teasingEnabledForce: boolean = false; // todo create getter for public access ?
    protected force: boolean = false;
    public reelsTeasingAttributes: Array<IReelTeasingAttributes> = [];
    public symbolsTeasingAttributes: Array<Array<ISymbolTeasingAttributes>> = [];

    private _customData: any;
    set customData(value: any) {
        this._customData = value;
    }

    constructor() {
        // default values
        this.reelCount = 5;
        this.teasingStopDelay = 90;
        this.minimumSymbolsForTeasing = 2;
        this.countSymbolsOnReelsAndRows = 0;
        this.canPlayTeaserSound = false;
        this.wasPlayedTeaserSound = false;
        this.wasPlayedTeaserWinSound = false;
        this.wasPlayedTeaserFailSound = false;

        this.finishTeasing = this.finishTeasing.bind(this);

        // CONFIGS
        let configs: Array<string> = ["GeneralConfiguration", "MachineConfiguration"];
        for (let i: number = 0; i < configs.length; i++) {
            let config: Element = Configuration.Configurations[configs[i]];
            if (config != null) {
                this.configurate(config);
            }
        }

        // REELS
        for (let i = 0; i < this.reelCount; i++) {
            this.reelsTeasingAttributes[i] = { action: ReelTeasingActions.DO_NOTHING, stopping: false };
        }

        // SYMBOLS
        for (let i = 0; i < this.reelCount; i++) {
            if (this.symbolsTeasingAttributes[i] == null) {
                this.symbolsTeasingAttributes[i] = [];
            }
            for (let j = 0; j < this._visibleSymbolCount; j++) {
                this.symbolsTeasingAttributes[i][j] = { teasable: false, action: SymbolTeasingActions.NOWIN };
            }
        }

        this._customData = [];
    }

    //******************************************************************************************************************
    //  CREATE, INITIALIZE
    //******************************************************************************************************************

    /**
     *
     * @param {Element} node
     */
    public create(node: Element): void {
        if (node.hasAttribute(TeasingComponentAttributes.CONFIGS)) {
            let configs: Array<string> = node.getAttribute(TeasingComponentAttributes.CONFIGS).split(";");
            for (let i: number = 0; i < configs.length; i++) {
                let config: Element = Configuration.Configurations[configs[i]];
                if (config != null) {
                    this.configurate(config);
                }
            }
        }
    }

    //******************************************************************************************************************
    //  CONFIGURATE
    //******************************************************************************************************************

    /**
     * Get attributes from configuration and set up supported properties.
     * @method configurate
     * @param node is Element from xml
     * @return void
     */
    private configurate(node: Element): void {
        if (node.hasAttribute(GeneralConfigurationAttributes.REEL_COUNT)) {
            this.reelCount = parseInt(node.getAttribute(GeneralConfigurationAttributes.REEL_COUNT));
        }

        if (node.hasAttribute(GeneralConfigurationAttributes.VISIBLE_SYMBOL_COUNT)) {
            this._visibleSymbolCount = parseInt(node.getAttribute(GeneralConfigurationAttributes.VISIBLE_SYMBOL_COUNT));
        }

        /*if (node.hasAttribute(MachineConfigurationAttributes.TEASING_SYMBOLS)) {
            this._teasingSymbols = ArrayUtils.numberArray(node.getAttribute(MachineConfigurationAttributes.TEASING_SYMBOLS));
        }*/

        if (node.hasAttribute(MachineConfigurationAttributes.TEASING_STOP_DELAY)) {
            this.teasingStopDelay = parseInt(node.getAttribute(MachineConfigurationAttributes.TEASING_STOP_DELAY));
        }

        if (node.hasAttribute(GeneralConfigurationAttributes.REEL_X)) {
            this.reelX = ArrayUtils.numberArray(node.getAttribute(GeneralConfigurationAttributes.REEL_X));
        }

        if (node.hasAttribute(GeneralConfigurationAttributes.REEL_Y)) {
            this.reelY = ArrayUtils.numberArray(node.getAttribute(GeneralConfigurationAttributes.REEL_Y));
        }

        if (node.hasAttribute(MachineConfigurationAttributes.STOP_DELAY)) {
            this.stopDelay = parseInt(node.getAttribute(MachineConfigurationAttributes.STOP_DELAY));
        }
    }

    /**
     *
     * @param {Object} element
     */
    public addElementInstance(element: Object): void {
        this.elementInstance[Object.keys(element)[0]] = element[Object.keys(element)[0]];
    }

    //******************************************************************************************************************
    //  CHECK REELS
    //******************************************************************************************************************

    /**
     * Check called before reel.spinStop(). Checks if teasing is possible and checks which reel can be stopped immediatly.
     * @param {ICheckOptions} options
     */
    public checkOnInit(options: ICheckOptions): Array<IReelTeasingAttributes> {
        this.stoppedSymbolsArray = options.stoppedSymbols;
        this.force = options.force;

        return this.reelsTeasingAttributes;
    }

    /**
     * Counts teasing symbols on reels [0..reelIndex] and decide if it has a sense to start teasing.
     * @returns {boolean}
     * @param reelIndexStart
     * @param reelIndexStop
     */
    protected isEnoughtForStartTeasing(reelIndexStart: number = 0, reelIndexStop: number): boolean {
        return (
            this.countSymbols(reelIndexStart, reelIndexStop, Settings.SymbolTeasing) >= this.minimumSymbolsForTeasing
        );
    }

    /**
     * Count symbols of some kind on reels.
     * @param {number} reelIndexStart
     * @param {number} reelIndexStop
     * @param symbolId
     * @returns {number}
     */
    public countSymbols(reelIndexStart: number = 0, reelIndexStop: number, symbolId: number): number {
        let count = 0;

        for (let i = reelIndexStart; i <= reelIndexStop; i++) {
            if (this.stoppedSymbolsArray[i].symbols.indexOf(symbolId) !== -1) {
                count++;
            }
        }

        return count;
    }

    /**
     * Custom conditions to setup reel as teasable.
     * @param {number} reelIndex
     * @returns {boolean}
     */
    protected isReelTeasable(reelIndex: number): boolean {
        //return this.teasingEnabled && reelIndex >= 2;
        return this.teasingEnabled;
    }

    /**
     * Return index of reel of first symbols occurence.
     * @returns {number}
     */
    private firstOccurrenceOfSymbol(reelIndexStart: number = 0, reelIndexStop: number): number {
        for (let i = reelIndexStart; i <= reelIndexStop; i++) {
            if (this.stoppedSymbolsArray[i].symbols.indexOf(Settings.SymbolTeasing) !== -1) {
                //return this._stoppedSymbolsArray[i].symbols.indexOf(Settings.SymbolTeasing);
                return i;
            }
        }

        return -1;
    }

    /**
     *
     * @param {number} reelIndex
     * @param {number} symbolId
     * @returns {number}
     */
    /*
    public firstOccurrenceOfSymbolOnReel(reelIndex: number, symbolId: number): number {
        if (this.stoppedSymbolsArray[reelIndex].symbols.indexOf(symbolId) !== -1) {
            //return this._stoppedSymbolsArray[i].symbols.indexOf(Settings.SymbolTeasing);
            return reelIndex;
        }

        return -1;
    }
    */

    protected precedingReelsWithSymbols(reelIndex: number, symbolId: number) {
        let count: number = 0;
        for (let i = 0; i < reelIndex; i++) {
            if (this.stoppedSymbolsArray[i].symbols.indexOf(symbolId) !== -1) {
                count++;
            }
        }
        return count;
    }

    /**
     *
     * @returns {Array<Array<ISymbolTeasingAttributes>>}
     */
    /*public checkSymbols(): Array<Array<ISymbolTeasingAttributes>> {
        if (this.teasingEnabled) {
            for (let i=0; i<this._reelCount; i++) {
                if (this.symbolsTeasingAttributes[i] != null) {
                    for (let j=0; j<this._visibleSymbolCount; j++) {
                        this.symbolsTeasingAttributes[i][j].teasable = true;  //todo
                    }
                }
            }
        }

        return this.symbolsTeasingAttributes;
    }*/

    /**
     *
     * @param {number} reelIndexStart
     * @param {number} reelIndexStop
     * @param {number} symbolId
     * @returns {number}
     */
    protected getCountSymbolsOnReelsAndRows(
        reelIndexStart: number = 0,
        reelIndexStop: number,
        symbolId: number,
    ): number {
        this.countSymbolsOnReelsAndRows = 0;

        for (let i = reelIndexStart; i <= reelIndexStop; i++) {
            for (let j = 0; j < this.stoppedSymbolsArray[i].symbols.length; j++) {
                if (this.stoppedSymbolsArray[i].symbols[j]) {
                    if (this.stoppedSymbolsArray[i].symbols[j] === symbolId) {
                        this.countSymbolsOnReelsAndRows++;
                    }
                }
            }
        }

        return this.countSymbolsOnReelsAndRows;
    }

    public getTeasingSymbol(): number {
        return this.teasingSymbol;
    }

    //******************************************************************************************************************
    //  CHECK SYMBOLS TEASING
    //******************************************************************************************************************

    /**
     *
     * @param {number} reelIndex
     * @returns {Array<ISymbolTeasingAttributes>}
     */
    public checkSymbolsPerReel(reelIndex: number): Array<Array<ISymbolTeasingAttributes>> {
        if (this.teasingEnabled) {
            let countOfSymbol = this.getCountSymbolsOnReelsAndRows(0, reelIndex, Settings.SymbolTeasing);
            /*
            0 - nowin
            1 - normal
            2 - impact
            3 - tease
            */
            switch (reelIndex) {
                case 0:
                    if (countOfSymbol > 1) {
                        for (let j = 0; j < this.stoppedSymbolsArray[reelIndex].symbols.length; j++) {
                            if (this.stoppedSymbolsArray[reelIndex].symbols[j]) {
                                if (this.stoppedSymbolsArray[reelIndex].symbols[j] === Settings.SymbolTeasing) {
                                    this.symbolsTeasingAttributes[reelIndex][j].action = SymbolTeasingActions.TEASE;
                                }
                            }
                        }
                    } else if (countOfSymbol == 1) {
                        for (let j = 0; j < this.stoppedSymbolsArray[reelIndex].symbols.length; j++) {
                            if (this.stoppedSymbolsArray[reelIndex].symbols[j]) {
                                if (this.stoppedSymbolsArray[reelIndex].symbols[j] === Settings.SymbolTeasing) {
                                    this.symbolsTeasingAttributes[reelIndex][j].action = SymbolTeasingActions.IMPACT;
                                }
                            }
                        }
                    } else {
                        for (let j = 0; j < this.stoppedSymbolsArray[reelIndex].symbols.length; j++) {
                            if (this.stoppedSymbolsArray[reelIndex].symbols[j]) {
                                if (this.stoppedSymbolsArray[reelIndex].symbols[j] === Settings.SymbolTeasing) {
                                    this.symbolsTeasingAttributes[reelIndex][j].action = SymbolTeasingActions.NORMAL;
                                }
                            }
                        }
                    }

                    break;
                case 1:
                    if (countOfSymbol > 1) {
                        for (let i = 0; i <= reelIndex; i++) {
                            for (let j = 0; j < this.stoppedSymbolsArray[i].symbols.length; j++) {
                                if (this.stoppedSymbolsArray[i].symbols[j]) {
                                    if (this.stoppedSymbolsArray[i].symbols[j] === Settings.SymbolTeasing) {
                                        this.symbolsTeasingAttributes[i][j].action = SymbolTeasingActions.IMPACT_TEASE;
                                    }
                                }
                            }
                        }
                    } else {
                        for (let i = 0; i <= reelIndex; i++) {
                            for (let j = 0; j < this.stoppedSymbolsArray[i].symbols.length; j++) {
                                if (this.stoppedSymbolsArray[i].symbols[j]) {
                                    if (this.stoppedSymbolsArray[i].symbols[j] === Settings.SymbolTeasing) {
                                        this.symbolsTeasingAttributes[i][j].action = SymbolTeasingActions.NOWIN;
                                    }
                                }
                            }
                        }
                    }

                    break;
                case 2:
                    if (countOfSymbol > 2) {
                        for (let i = 0; i <= reelIndex; i++) {
                            for (let j = 0; j < this.stoppedSymbolsArray[i].symbols.length; j++) {
                                if (this.stoppedSymbolsArray[i].symbols[j]) {
                                    if (this.stoppedSymbolsArray[i].symbols[j] === Settings.SymbolTeasing) {
                                        if (i == reelIndex) {
                                            this.symbolsTeasingAttributes[i][j].action =
                                                SymbolTeasingActions.IMPACT_TEASE;
                                        } else {
                                            this.symbolsTeasingAttributes[i][j].action = SymbolTeasingActions.TEASE;
                                        }
                                    }
                                }
                            }
                        }
                    } else {
                        this.teasingEnabled = false;
                        for (let i = 0; i <= reelIndex; i++) {
                            for (let j = 0; j < this.stoppedSymbolsArray[i].symbols.length; j++) {
                                if (this.stoppedSymbolsArray[i].symbols[j]) {
                                    this.symbolsTeasingAttributes[i][j].action = SymbolTeasingActions.NORMAL;
                                }
                            }
                        }
                    }
                    break;
                case 3:
                    if (countOfSymbol > 3) {
                        for (let i = 0; i <= reelIndex; i++) {
                            for (let j = 0; j < this.stoppedSymbolsArray[i].symbols.length; j++) {
                                if (this.stoppedSymbolsArray[i].symbols[j]) {
                                    if (this.stoppedSymbolsArray[i].symbols[j] === Settings.SymbolTeasing) {
                                        if (i == reelIndex) {
                                            this.symbolsTeasingAttributes[i][j].action =
                                                SymbolTeasingActions.IMPACT_TEASE;
                                        } else {
                                            this.symbolsTeasingAttributes[i][j].action = SymbolTeasingActions.TEASE;
                                        }
                                    }
                                }
                            }
                        }
                    } else if (countOfSymbol == 3) {
                        for (let i = 0; i <= reelIndex; i++) {
                            for (let j = 0; j < this.stoppedSymbolsArray[i].symbols.length; j++) {
                                if (this.stoppedSymbolsArray[i].symbols[j]) {
                                    if (this.stoppedSymbolsArray[i].symbols[j] === Settings.SymbolTeasing) {
                                        this.symbolsTeasingAttributes[i][j].action = SymbolTeasingActions.TEASE;
                                    }
                                }
                            }
                        }
                    } else {
                        for (let i = 0; i <= reelIndex; i++) {
                            for (let j = 0; j < this.stoppedSymbolsArray[i].symbols.length; j++) {
                                if (this.stoppedSymbolsArray[i].symbols[j]) {
                                    this.symbolsTeasingAttributes[i][j].action = SymbolTeasingActions.NORMAL;
                                }
                            }
                        }
                    }
                    break;
                case 4:
                    if (countOfSymbol > 4) {
                        for (let i = 0; i <= reelIndex; i++) {
                            for (let j = 0; j < this.stoppedSymbolsArray[i].symbols.length; j++) {
                                if (this.stoppedSymbolsArray[i].symbols[j]) {
                                    if (this.stoppedSymbolsArray[i].symbols[j] === Settings.SymbolTeasing) {
                                        if (i == reelIndex) {
                                            this.symbolsTeasingAttributes[i][j].action =
                                                SymbolTeasingActions.IMPACT_TEASE;
                                        } else {
                                            this.symbolsTeasingAttributes[i][j].action = SymbolTeasingActions.TEASE;
                                        }
                                    }
                                }
                            }
                        }
                    } else if (countOfSymbol == 3 || countOfSymbol == 4) {
                        for (let i = 0; i <= reelIndex; i++) {
                            for (let j = 0; j < this.stoppedSymbolsArray[i].symbols.length; j++) {
                                if (this.stoppedSymbolsArray[i].symbols[j]) {
                                    if (this.stoppedSymbolsArray[i].symbols[j] === Settings.SymbolTeasing) {
                                        this.symbolsTeasingAttributes[i][j].action = SymbolTeasingActions.TEASE;
                                    }
                                }
                            }
                        }
                    } else {
                        for (let i = 0; i <= reelIndex; i++) {
                            for (let j = 0; j < this.stoppedSymbolsArray[i].symbols.length; j++) {
                                if (this.stoppedSymbolsArray[i].symbols[j]) {
                                    this.symbolsTeasingAttributes[i][j].action = SymbolTeasingActions.NORMAL;
                                }
                            }
                        }
                    }
                    break;
            }
        } else {
            for (let i = 0; i <= reelIndex; i++) {
                for (let j = 0; j < this.stoppedSymbolsArray[i].symbols.length; j++) {
                    this.symbolsTeasingAttributes[i][j].action = SymbolTeasingActions.NORMAL;
                }
            }
        }

        return this.symbolsTeasingAttributes;
    }

    /**
     *
     * @param {number} reelIndex
     * @returns {Array<IReelTeasingAttributes>}
     */
    /*public updateReelTeasingAttributes(reelIndex: number): Array<IReelTeasingAttributes> {
        if (!this._force) {
            this.reelsTeasingAttributes[0].action = ReelTeasingActions.DO_NOTHING;
            this.reelsTeasingAttributes[1].action = ReelTeasingActions.DO_NOTHING;
            this.reelsTeasingAttributes[2].action = ReelTeasingActions.DO_NOTHING;
            this.reelsTeasingAttributes[3].action = ReelTeasingActions.DO_NOTHING;
            this.reelsTeasingAttributes[4].action = ReelTeasingActions.DO_NOTHING;

            switch (reelIndex) {
                case 0:
                    this.reelsTeasingAttributes[1].action = ReelTeasingActions.SPIN_STOP;
                    break;
                case 1:
                    if (this.firstOccurrenceOfSymbolOnReel(0, Settings.SymbolTeasing) !== -1
                        && this.firstOccurrenceOfSymbolOnReel(1, Settings.SymbolTeasing) !== -1) {

                        this.reelsTeasingAttributes[2].action = ReelTeasingActions.TEASE;
                        this.teasingEnabled = true;
                    } else {
                        this.reelsTeasingAttributes[2].action = ReelTeasingActions.SPIN_STOP;
                        this.teasingEnabled = false;
                    }
                    break;
                case 2:
                    if (this.firstOccurrenceOfSymbolOnReel(0, Settings.SymbolTeasing) !== -1
                        && this.firstOccurrenceOfSymbolOnReel(1, Settings.SymbolTeasing) !== -1
                        && this.firstOccurrenceOfSymbolOnReel(2, Settings.SymbolTeasing) !== -1) {

                        this.reelsTeasingAttributes[3].action = ReelTeasingActions.TEASE;
                        this.teasingEnabled = true;
                    } else {
                        this.reelsTeasingAttributes[3].action = ReelTeasingActions.SPIN_STOP;
                        this.teasingEnabled = false;
                    }
                    break;
                case 3:
                    if (this.firstOccurrenceOfSymbolOnReel(0, Settings.SymbolTeasing) !== -1
                        && this.firstOccurrenceOfSymbolOnReel(1, Settings.SymbolTeasing) !== -1
                        && this.firstOccurrenceOfSymbolOnReel(2, Settings.SymbolTeasing) !== -1
                        && this.firstOccurrenceOfSymbolOnReel(3, Settings.SymbolTeasing) !== -1) {

                        this.reelsTeasingAttributes[4].action = ReelTeasingActions.TEASE;
                        this.teasingEnabled = true;
                    } else {
                        this.reelsTeasingAttributes[4].action = ReelTeasingActions.SPIN_STOP;
                        this.teasingEnabled = false;
                    }
                    break;
                case 4:
                    break;
            }
        }

        return this.reelsTeasingAttributes;
    }*/

    //******************************************************************************************************************
    //  REELS TEASING ANIMATIONS
    //******************************************************************************************************************

    /**
     * Start animating teasing layers.
     *
     * @param {number} reelIndex
     */
    public animateTeasing(reelIndex: number): void {}

    /**
     *
     */
    public finishTeasing(): void {
        //PlayList.loop('SND_BaseLoop');

        for (let i = 0; i <= this.reelCount - 1; i++) {
            for (let j = 0; j < this.stoppedSymbolsArray[i].symbols.length; j++) {
                if (this.stoppedSymbolsArray[i].symbols[j]) {
                    this.symbolsTeasingAttributes[i][j].action = SymbolTeasingActions.NOWIN; //TODO Reset to NO_WIN or -1?
                }
            }
        }

        this.canPlayTeaserSound = false;
        this.wasPlayedTeaserSound = false;
        this.wasPlayedTeaserWinSound = false;
        this.wasPlayedTeaserFailSound = false;
    }

    /**
     * Remove teasing animations
     *
     * @param {boolean} noWin
     */
    public removeTeasing(noWin: boolean): void {
        if (noWin) {
            // PlayList.play(this._playList, 'SND_BaseFSTeaserFail');
        }
    }

    //******************************************************************************************************************
    //  LOG
    //******************************************************************************************************************

    /**
     * Trace to browser console.
     * @method log
     * @param s string
     * @return void
     */
    protected log(s: string): void {
        if (DEBUG) {
            let compName = (this as any).__componentName__;
            Logger.debugGroup(compName, s);
        }
    }
}

const TeasingComponentAttributes = {
    CONFIGS: "Configs",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/TeasingComponent.ts
 **/